import { createContext, useContext, useEffect, useState } from "react";
import { auth as authApi } from "@/lib/api";
import {
  getToken, getUser, isRemembered, saveSession, updateSession, clearSession, needsRenewal,
} from "@/lib/session-store";

const AuthCtx = createContext(null);

// How often an open app checks whether its session is due for renewal. The
// check is local (the token's own exp); a request goes out only when it is.
const RENEW_CHECK_MS = 30 * 60 * 1000;

/** Hand the session to the desktop app's main process, which posts telemetry
 *  with it. `remember: false` keeps it in main's memory only. No-op in a browser. */
function shareWithDesktop(token, user) {
  if (typeof window === "undefined" || !window.electronAPI) return;
  window.electronAPI.session.set({
    token,
    apiBase: process.env.REACT_APP_BACKEND_URL,
    user,
    remember: isRemembered(),
  });
}

/** Renew the token if it is past half its life. Sliding renewal, so a
 *  remembered pilot who opens the app at least once a month stays signed in.
 *  A refusal (403 closed beta, 401 password changed) signs out through the
 *  api.js 401 handler or at the next start; a network error just waits. */
async function renewIfDue() {
  const token = getToken();
  if (!token || !needsRenewal(token)) return;
  try {
    const { token: next, user } = await authApi.refresh();
    updateSession({ token: next, user });
    shareWithDesktop(next, user);
  } catch { /* offline, or refused: handled elsewhere */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getUser());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      setReady(true);
      return;
    }
    authApi
      .me()
      .then((u) => {
        setUser(u);
        updateSession({ user: u });
        return renewIfDue();
      })
      .catch(() => {
        clearSession();
        setUser(null);
      })
      .finally(() => setReady(true));
  }, []);

  // While the app is open: renew in the background before the session runs out.
  useEffect(() => {
    if (!user) return undefined;
    const timer = setInterval(renewIfDue, RENEW_CHECK_MS);
    return () => clearInterval(timer);
  }, [user]);

  const login = async (login, password, remember = false) => {
    const { token, user: u } = await authApi.login(login, password, remember);
    saveSession(token, u, remember);
    setUser(u);
    shareWithDesktop(token, u);
    return u;
  };

  const logout = () => {
    clearSession();
    setUser(null);
    if (typeof window !== "undefined" && window.electronAPI) {
      window.electronAPI.session.clear();
    }
  };

  return (
    <AuthCtx.Provider value={{ user, login, logout, ready }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
